import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { toast } from '@/hooks/useToast';

/**
 * Shown exactly once, right after setup or regeneration: the server keeps only
 * hashes, so nobody (including an admin) can show these again.
 */
export function RecoveryCodesPanel({ codes, account, onDone, doneLabel = 'Continue' }: {
  codes: string[];
  account: string;
  onDone: () => void;
  doneLabel?: string;
}) {
  const [saved, setSaved] = useState(false);
  const text = `Recovery codes for ${account} (two-step login)\nEach code works once.\n\n${codes.join('\n')}\n`;

  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `recovery-codes-${account}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-ink">
        If you lose your phone, each of these codes lets you sign in once. Save them somewhere safe, away from your phone.
        They won’t be shown again.
      </p>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-(--radius) border border-border bg-canvas p-3 font-mono text-[13px] text-ink">
        {codes.map((c) => <li key={c}>{c}</li>)}
      </ul>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={download}>Download .txt</Button>
        <Button type="button" variant="ghost" size="sm" onClick={async () => {
          try { await navigator.clipboard.writeText(text); toast({ title: 'Copied', variant: 'success' }); }
          catch { toast({ title: 'Couldn’t copy — use Download instead', variant: 'danger' }); }
        }}>Copy</Button>
      </div>
      <label className="flex items-center gap-2 text-[13px] text-ink">
        <Checkbox checked={saved} onCheckedChange={(v) => setSaved(v === true)} />
        I’ve saved these codes
      </label>
      <Button type="button" disabled={!saved} onClick={onDone}>{doneLabel}</Button>
    </div>
  );
}
